// #region imports
import React, { useCallback, useEffect, useState } from 'react';

// helpers
import { EditNewProjectMetadataModal } from '../../Projects/Modals/EditNewProjectMetadataModal';
import { EditMetadataFormSmart } from '../../ContentPane/DetailView/MetaData/EditMetadataFormSmart';

// Types

// HOCS
import { useModalActions } from '../../../layout/Modals/modalActions';
import { useMultiForm } from '../../MetaData/hooks/useMultiForm';
import { useMappedId__ALPHA } from '@cube3/state/src/redux/components/Hooks/useCreateResource';
import { useTypedSelector } from '@cube3/state/src/redux/components/Hooks/useTypedSelector';
import { getErrorText } from '../../Projects/Modals/AddProjectModal';
import Cube3Button from '@cube3/ui/src/Buttons/Cube3Button';
import { generatePath, useHistory } from 'react-router-dom';
import { urlStructureProject } from '../../../routing/routingPaths';
import { useCurrentWorkspace } from '@cube3/state/src/redux/components/Administration/withCurrentWorkspace';
import {
  iconArrowDown,
  iconChevronRight
} from '@cube3/ui/src/icons/GeneralSVG';
import { getFormError } from 'redux-form';
import { useProjectMetadataFields } from '../../Projects/hooks/useProjectMetadataFields';
import { useGenerateProjectNameByMetadataValues } from '../../Projects/hooks/useGenerateProjectName';
import { useDeleteResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useDeleteResource';

interface ModalProps {
  modalContext?: {
    createProject: (e: React.SyntheticEvent) => void;
    filterValues: any;
    tempId: string;
    draftProjectId?: string;
  };
}

function EditNewProjectMetadata(props: ModalProps): JSX.Element {
  const {
    createProject,
    filterValues = {},
    tempId,
    draftProjectId
  } = props.modalContext;

  const [workspaceId] = useCurrentWorkspace();

  const { openModal, previousModal, closeAllModals } = useModalActions();
  const history = useHistory();
  const [shouldSubmit, setShouldSubmit] = useState(false);
  const [showAllFields, setShowAllFields] = useState(false);
  const [createProjectLoading, setCreateProjectLoading] = useState(undefined);

  const errors = useTypedSelector((state) =>
    getFormError('add_project')(state)
  );
  const mappedId = useMappedId__ALPHA(tempId);

  const { retrievedForms, prefillMetadataCheck } = useProjectMetadataFields({
    filterValues
  });
  const { hasRequiredFields } = prefillMetadataCheck;

  const metadataFor = React.useRef({
    type: 'project',
    id: undefined
  });

  const {
    allFormsValid,
    submitAllForms,
    allFormsSucceeded,
    allFormsPristine,
    formSubmitting,
    destroyForms
  } = useMultiForm(retrievedForms, true);

  const errorMsg = !mappedId ? errors && getErrorText(errors) : undefined;

  /**
   * - `project_name_string_template` flow: submit metadata values
   * - normal flow: create the actual project -> submit metadata values
   */
  const handleClickCreate = useCallback(
    (e) => {
      if (draftProjectId) {
        metadataFor.current.id = draftProjectId;
      } else if (!mappedId) {
        createProject(e);
        setCreateProjectLoading(true);
      }
      setShouldSubmit(true);
    },
    [createProject, mappedId, draftProjectId]
  );

  useEffect(() => {
    if (mappedId) {
      setCreateProjectLoading(false);
    }
    if (!metadataFor.current.id && mappedId) {
      metadataFor.current.id = mappedId;
    }
    setCreateProjectLoading(undefined);
  }, [mappedId]);

  // after created new project, submit metadata values and attach to the new created project or the draft project
  useEffect(() => {
    if (shouldSubmit && (mappedId || draftProjectId)) {
      submitAllForms();
      setShouldSubmit(false);
    }
  }, [mappedId, shouldSubmit, draftProjectId, submitAllForms, setShouldSubmit]);

  const [deleteProject] = useDeleteResource__ALPHA({
    cacheInvalidator: (r) => [r]
  });

  /** Handle user clicks outside of the modal */
  const handleClose = useCallback(() => {
    const onClose = () => {
      if (formSubmitting) return;
      if (draftProjectId) {
        // if the user cancels anytime before the final submit, the draft project should get deleted
        deleteProject({
          type: 'project',
          id: draftProjectId
        });
      }
      closeAllModals();
      destroyForms();
    };
    if (!allFormsPristine) {
      openModal(
        'integration_discard_config_changes',
        {
          onCancel: previousModal,
          onConfirm: onClose
        },
        false
      );
    } else {
      onClose();
    }
  }, [
    formSubmitting,
    allFormsPristine,
    draftProjectId,
    deleteProject,
    destroyForms
  ]);

  const prefilledProjectName =
    useGenerateProjectNameByMetadataValues(retrievedForms);

  /** After saving metadata values,
   * - if `draftProjectId` is provided, open `AddProjectModal` to update the draft project properties,
   * - otherwise redirect to the created project.
   */
  const handleNextStep = useCallback(() => {
    if (draftProjectId) {
      openModal(
        'projects_add',
        { prefilledProjectName, draftProjectId },
        false
      );
    } else if (mappedId) {
      history.push(
        generatePath(urlStructureProject, {
          workspaceId,
          projectId: mappedId
        })
      );
      closeAllModals();
      destroyForms();
    }
  }, [draftProjectId, prefilledProjectName, mappedId]);

  return (
    <EditNewProjectMetadataModal
      title={`Enter ${hasRequiredFields ? 'required' : ''} project metadata`}
      onCloseEvent={handleClose}
      hasRequiredFields={hasRequiredFields}
      hasPrefilledFields={Object.keys(filterValues).length > 0}
      onClickPrevious={previousModal}
      loading={!!createProjectLoading || formSubmitting}
      retrievedForms={retrievedForms}
      allFormsValid={allFormsValid}
      allFormsSucceeded={allFormsSucceeded && !formSubmitting}
      onNext={handleNextStep}
      hasProjectNameTemplateFeature={!!draftProjectId}
      onClickCreate={handleClickCreate}
      renderMetaDataForm={(form) => (
        <EditMetadataFormSmart
          metadataFormId={form.id}
          metadataForResource={metadataFor.current}
          metadataForm={form}
          requiredFieldsOnly={!showAllFields}
          prefilledValues={filterValues}
          useDefault={true}
        />
      )}
      error={errorMsg}
    >
      <div style={{ paddingBottom: 20 }}>
        <Cube3Button
          colorVariant="ghost1"
          text={
            !showAllFields
              ? 'Show all project metadata fields'
              : 'Hide fields that are not required'
          }
          pathRightSvg={!showAllFields ? iconChevronRight : iconArrowDown}
          extraSVGProps={
            !showAllFields && { svgProps: { viewBox: '0 0 32 32' } }
          }
          onClick={() => setShowAllFields(!showAllFields)}
        />
      </div>
    </EditNewProjectMetadataModal>
  );
}

export default EditNewProjectMetadata;
